import {query} from "@niitsolutions/kru/fetcher";
import {currentOrganizationPrefix, publicUrl, request} from "./common";

// ---------------------------- KRU VALUES ------------------------------

export async function getKruvalues({query = {}, options = {}} = {}) {
  const url = publicUrl(`kruvalues`);
  return request({url, body: {query, options}});
}

export async function getKruValuesByCategory(category, {options = {limit: 0}} = {}) {
  return getKruvalues({query: {category: {$in: category}}, options});
}

// ---------------------------- RESULT PROVIDERS ------------------------------

export async function getResultProviders({query = {}, options = {}} = {}) {
  const url = publicUrl(`resultproviders`);
  return request({url, body: {query, options}});
}

// ---------------------------------- DOGS ------------------------------

export async function getTreedogs(
  { organizationPrefix,
    query = {
      organizations: {
        $elemMatch: {
          prefix: organizationPrefix,
          "metadata.verifiedObject": false,
        }
      }
    },
    options = { populate: "organizations.organization" } } = {}
) {
  const url = publicUrl(`${organizationPrefix}/dogs`);
  return request({url, body: {query, options}});
}

export async function getDogs(
  { organizationPrefix,
    query = {},
    options = { populate: "organizations.organization" } } = {}
) {
  const url = publicUrl(`${organizationPrefix}/dogs`);
  query = {
    ...query,
    organizations: {
      $elemMatch: {
        prefix: organizationPrefix,
        "metadata.verifiedObject": true,
      }
    }
  };
  return request({url, body: {query, options}});
}

export async function getRegisteredDogs({
  organizationPrefix,
  query = {},
  options = {
    populate: "organizations.organization",
  },
}) {
  const url = publicUrl(`${organizationPrefix}/dogs`);
  query = {
    ...query,
    organizations: {
      $elemMatch: {
        prefix: organizationPrefix,
        regNumber: { $exists: true, $ne: null },
      }
    },
  };
  return await request({url, body: {query, options}});
}

export async function getPendingDogs({
  organizationPrefix,
  query = {
    organizations: {
      $elemMatch: {
        prefix: organizationPrefix,
        regRequest: { $in: [null, ""] },
      }
    }
  },
  options = {
    populate: "organizations.organization",
  },
  } = {}) {
  const url = publicUrl(`${organizationPrefix}/dogs`);
  return request({url, body: {query, options}});
}

export async function getDogByOrgRegNumber({
  regNumber = "",
  options = {
    maxDepth: 4,
    populate: "breeder owners dogOwners sire dam attachments.images organizations.organization certificates.certificate",
  },
} = {}) {
  const organizationPrefix = currentOrganizationPrefix();
  const url = publicUrl(`${organizationPrefix}/dog/${regNumber}`);
  return request({url, body: {options}});
}

export async function getDogsBySex(sex) {
  return getDogs({query: {sex}});
}

// ----------------------------- ORGANIZATIONS ------------------------------

export async function getAllOrganizations({query = {}, options = {}} = {}) {
  const url = publicUrl(`organizations`);
  return request({url, body: {query, options}});
}

export async function getOrganizationById(_id, {options = {}} = {}) {
  if (!_id) return;
  if (typeof _id !== "string") {
    throw new Error("getOrganizationById requires ID in string format");
  }
  const url = publicUrl(`organizations/byId/${_id}`);
  return request({url, body: {options}});
}

export async function getOrganizationByPrefix(_prefix, {options = {}} = {}) {
  if (!_prefix) return;
  if (typeof _prefix !== "string") {
    throw new Error("getOrganizationByprefix requires prefix in string format");
  }
  const url = publicUrl(`organizations`);
  const orgArray = await request({
    url,
    body: {
      query: {
        prefix: _prefix,
      },
    },
  });

  if (orgArray.length > 0) {
    return orgArray[0];
  }
  return null;
}

// ----------------------------- CERTIFICATES ------------------------------

export async function getCertificateById(_id, {options = {}} = {}) {
  const url = publicUrl(`certificates/byId/${_id}`);
  return request({url, body: {options}});
}

// ---------------------------------- USER ------------------------------

export async function getAllUsers({body = {}} = {}) {
  const url = publicUrl(`users`);
  return request({url, body});
}

export async function getBreederByQuery({body = {}} = {}) {
  const url = publicUrl(`users`);
  return request({url, body});
}

export async function getBreederById(_id, {options = {}} = {}) {
  const url = publicUrl(`users/byId/${_id}`);
  return request({url, body: {options}});
}

export async function getBreedersWithLitters({options = {}} = {}) {
  const url = publicUrl(`breedersWithLitters`);
  return request({url, body: {options}});
}

// ---------------------------------- LITTERS ------------------------------

export async function getLitters({query = {}, options = {}} = {}) {
  const url = publicUrl(`litters`);
  return request({url, body: {query, options}});
}

export async function getOrganizationLitters({
  org = {org},
  options = {
    maxDepth: 4,
    populate: "breeder sire dam organizations.organization",
  },
}) {
  const url = publicUrl(`litters`);
  // Was `query = {...}` (no declaration) -- reassigning the module-level `query` imported from
  // @niitsolutions/kru/fetcher at the top of this file, which throws (imported bindings are
  // read-only) every time this function was called. Fixed while touching this line for FDD-227.
  const query = {
    organizations: {
      $elemMatch: {
        prefix: org.prefix,
        status: "accepted",
      },
    },
  };
  return await request({url, body: {query, options}});
}

export async function getLitterById(
  _id,
  {
    options = {
      maxDepth: 4,
      populate: "sire dam breeder organizations.organization certificates.certificate",
    },
  } = {},
) {
  const url = publicUrl(`litters/byId/${_id}`);
  return request({url, body: {options}});
}

export async function getLittersByBreederId(id) {
  const url = publicUrl(`litters`);
  return request({
    url,
    body: {
      maxDepth: 4,
      options: {
        populate: "sire dam organizations.organization certificates.certificate",
      },
      query: {breeder: id},
    },
  });
}

export async function getLittersByParentId(id, sex) {
  const url = publicUrl(`litters`);
  const key = sex === "female" ? "dam" : "sire";
  return request({
    url,
    body: {
      options: {
        populate: "sire dam organizations.organization certificates.certificate",
      },
      query: {[key]: id},
    },
  });
}

export async function getLitterBySiblingId(_id) {
  const url = publicUrl(`litters`);
  return request({
    url,
    body: {
      maxDepth: 4,
      options: {
        populate: "sire dam organizations.organization certificates.certificate",
      },
      query: {puppies: _id},
    },
  });
}

// ---------------------------------- PEDIGREE ------------------------------

export async function getDogById(
  _id,
  {
    options = {
      maxDepth: 4,
      populate: "sire dam owners dogOwners organizations.organization",
    },
  } = {},
) {
  const organizationPrefix = currentOrganizationPrefix();
  const url = publicUrl(`${organizationPrefix}/dogs/byId/${_id}`);
  return await request({url, body: {options}});
}

export async function getDogWithPedigree(_id, maxDepth = 4) {
  return getDogById(_id, {
    options: {maxDepth: 4, autopopulate: true, maxDepth, lean: false},
  });
}

// ---------------------------------- SEARCH ------------------------------

export async function getSearchResults(query, options = {}) {
  const trimmed = String(query ?? "").trim();
  if (!trimmed) return [];
  const organizationPrefix = currentOrganizationPrefix();
  const url = publicUrl(`${organizationPrefix}/search/dogs`);
  return request({url, body: {query: trimmed, options}});
}

// ---------------------------------- RESULT PROVIDERS ------------------------------

export async function getResultproviders({query = {}, options = {}} = {}) {
  const url = publicUrl(`resultproviders`);
  return request({url, body: {query, options}});
}

export async function getResultProviderById(_id, {options = {}} = {}) {
  const url = publicUrl(`resultproviders/byId/${_id}`);
  return request({url, body: {options}});
}
